/**
 * What the governance enums are called on screen.
 *
 * Each screen printed the raw value, so a reader met "awaiting-approval" in one
 * place and "awaiting approval" in the next. `Record` over the union, so a new
 * value fails to compile until it has a name.
 *
 * @module components/shared/governance-labels
 */

import type {
  AmendmentClassification,
  AmendmentStatus,
  CurriculumChangeCaseKind,
  CurriculumChangeCaseStatus,
  CurriculumRevisionKind,
  IdentityImpactOutcome,
  ReviewOutcome,
} from "@/server/types";

const CHANGE_CASE_STATUS: Record<CurriculumChangeCaseStatus, string> = {
  "team-confirmation": "Team confirmation required",
  open: "Open",
  submitted: "In internal review",
  "awaiting-approval": "Awaiting approval",
  "changes-required": "Changes required",
  approved: "Approved",
  "replacement-created": "Replacement created",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

const CLASSIFICATION: Record<AmendmentClassification, string> = {
  administrative: "Administrative",
  minor: "Minor",
  major: "Major",
};

const AMENDMENT_STATUS: Record<AmendmentStatus, string> = {
  proposed: "Proposed",
  approved: "Approved",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

const REVIEW_OUTCOME: Record<ReviewOutcome, string> = {
  "change-request": "Changes requested",
  approved: "Approved",
  commented: "Commented",
};

const CHANGE_CASE_KIND: Record<CurriculumChangeCaseKind, string> = {
  amendment: "Amendment",
  revalidation: "Revalidation",
};

const REVISION_KIND: Record<CurriculumRevisionKind, string> = {
  initial: "Initial validation",
  amendment: "Amendment",
  revalidation: "Revalidation",
};

const IDENTITY_IMPACT: Record<IdentityImpactOutcome, string> = {
  "continue-same-programme": "Continue as the same programme",
  "create-replacement-programme": "Create a replacement programme",
};

export function changeCaseStatusLabel(status: CurriculumChangeCaseStatus): string {
  return CHANGE_CASE_STATUS[status];
}

/** What the change itself is called; "Change" until its own case has loaded. */
export function changeCaseKindLabel(kind: CurriculumChangeCaseKind | undefined): string {
  return kind ? CHANGE_CASE_KIND[kind] : "Change";
}

/** The same noun mid-sentence: "Approve this revalidation?" */
export function changeCaseKindNoun(kind: CurriculumChangeCaseKind | undefined): string {
  return changeCaseKindLabel(kind).toLowerCase();
}

/** "Minor amendment"; an amendment with no classification yet says so. */
export function amendmentLabel(classification: AmendmentClassification | undefined): string {
  return `${classification ? CLASSIFICATION[classification] : "Unclassified"} amendment`;
}

export function amendmentStatusLabel(status: AmendmentStatus): string {
  return AMENDMENT_STATUS[status];
}

export function identityImpactLabel(outcome: IdentityImpactOutcome): string {
  return IDENTITY_IMPACT[outcome];
}

export function reviewOutcomeLabel(outcome: ReviewOutcome): string {
  return REVIEW_OUTCOME[outcome];
}

export function revisionKindLabel(kind: CurriculumRevisionKind): string {
  return REVISION_KIND[kind];
}
